import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, EyeIcon, DownloadIcon, FilterIcon, CreditCardIcon, QrCodeIcon, SmartphoneIcon, FileTextIcon, TerminalIcon, RefreshCwIcon, MonitorIcon } from 'lucide-react';
import { Sale, TransactionSource, TransactionStatus } from '../types/Transaction';
export function Sales() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState('7d');
  // Mock data - in a real app, this would come from an API
  const sales: Sale[] = [{
    id: '1',
    transactionId: 'TXN-2024-001234',
    amount: 15000,
    currency: 'LKR',
    status: 'completed',
    source: 'payment_link',
    date: '2024-01-15T10:30:00',
    customerName: 'Nimal Perera',
    customerEmail: 'nimal@example.com',
    customerPhone: '+94 77 123 4567',
    paymentMethod: {
      type: 'card',
      last4: '4242',
      brand: 'Visa'
    },
    merchantName: 'ABC Store',
    merchantId: 'MERCH-001',
    description: 'Online purchase',
    fee: 450,
    netAmount: 14550,
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-15T10:30:00',
      status: 'initiated',
      description: 'Transaction initiated'
    }, {
      id: '2',
      timestamp: '2024-01-15T10:30:15',
      status: 'processing',
      description: 'Payment processing'
    }, {
      id: '3',
      timestamp: '2024-01-15T10:30:45',
      status: 'completed',
      description: 'Payment successful'
    }]
  }, {
    id: '2',
    transactionId: 'TXN-2024-001235',
    amount: 8500,
    currency: 'LKR',
    status: 'completed',
    source: 'qr_code',
    date: '2024-01-15T11:45:00',
    customerName: 'Kamala Silva',
    customerEmail: 'kamala@example.com',
    paymentMethod: {
      type: 'wallet',
      details: 'eZ Cash'
    },
    merchantName: 'XYZ Restaurant',
    merchantId: 'MERCH-002',
    description: 'Dine-in payment',
    fee: 255,
    netAmount: 8245,
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-15T11:45:00',
      status: 'initiated',
      description: 'QR code scanned'
    }, {
      id: '2',
      timestamp: '2024-01-15T11:45:10',
      status: 'completed',
      description: 'Payment successful'
    }]
  }, {
    id: '3',
    transactionId: 'TXN-2024-001236',
    amount: 25000,
    currency: 'LKR',
    status: 'refunded',
    source: 'invoice',
    date: '2024-01-14T14:20:00',
    customerName: 'Saman Fernando',
    customerEmail: 'saman@example.com',
    customerPhone: '+94 71 987 6543',
    paymentMethod: {
      type: 'card',
      last4: '5555',
      brand: 'Mastercard'
    },
    merchantName: 'Tech Solutions Ltd',
    merchantId: 'MERCH-003',
    description: 'Software subscription',
    invoiceId: 'INV-2024-001',
    refundAmount: 25000,
    fee: 750,
    netAmount: 0,
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-14T14:20:00',
      status: 'initiated',
      description: 'Invoice payment initiated'
    }, {
      id: '2',
      timestamp: '2024-01-14T14:20:30',
      status: 'completed',
      description: 'Payment successful'
    }, {
      id: '3',
      timestamp: '2024-01-15T09:15:00',
      status: 'refund_initiated',
      description: 'Refund requested'
    }, {
      id: '4',
      timestamp: '2024-01-15T09:20:00',
      status: 'refunded',
      description: 'Refund completed'
    }]
  }, {
    id: '4',
    transactionId: 'TXN-2024-001237',
    amount: 3500,
    currency: 'LKR',
    status: 'completed',
    source: 'tap_to_phone',
    date: '2024-01-15T16:30:00',
    customerName: 'Priya Jayawardena',
    customerEmail: 'priya@example.com',
    paymentMethod: {
      type: 'card',
      last4: '8888',
      brand: 'Visa'
    },
    merchantName: 'Coffee Shop',
    merchantId: 'MERCH-004',
    description: 'Contactless payment',
    fee: 105,
    netAmount: 3395,
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-15T16:30:00',
      status: 'initiated',
      description: 'Card tapped'
    }, {
      id: '2',
      timestamp: '2024-01-15T16:30:05',
      status: 'completed',
      description: 'Payment successful'
    }]
  }, {
    id: '5',
    transactionId: 'TXN-2024-001238',
    amount: 45000,
    currency: 'LKR',
    status: 'failed',
    source: 'pos',
    date: '2024-01-15T17:00:00',
    customerName: 'Ranil Wickramasinghe',
    customerEmail: 'ranil@example.com',
    paymentMethod: {
      type: 'card',
      last4: '3333',
      brand: 'Mastercard'
    },
    merchantName: 'Electronics Store',
    merchantId: 'MERCH-005',
    description: 'POS terminal payment',
    fee: 0,
    netAmount: 0,
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-15T17:00:00',
      status: 'initiated',
      description: 'POS transaction initiated'
    }, {
      id: '2',
      timestamp: '2024-01-15T17:00:10',
      status: 'processing',
      description: 'Contacting bank'
    }, {
      id: '3',
      timestamp: '2024-01-15T17:00:20',
      status: 'failed',
      description: 'Insufficient funds'
    }]
  }];
  const getSourceIcon = (source: TransactionSource) => {
    const icons = {
      payment_link: <CreditCardIcon size={16} data-id="element-5333" />,
      qr_code: <QrCodeIcon size={16} data-id="element-5334" />,
      tap_to_phone: <SmartphoneIcon size={16} data-id="element-5335" />,
      invoice: <FileTextIcon size={16} data-id="element-5336" />,
      pos: <TerminalIcon size={16} data-id="element-5337" />,
      recurring: <RefreshCwIcon size={16} data-id="element-5338" />,
      virtual_terminal: <MonitorIcon size={16} data-id="element-5339" />
    };
    return icons[source];
  };
  const getSourceLabel = (source: TransactionSource) => {
    const labels = {
      payment_link: 'Payment Link',
      qr_code: 'QR Code',
      tap_to_phone: 'Tap to Phone',
      invoice: 'Invoice',
      pos: 'POS Terminal',
      recurring: 'Recurring',
      virtual_terminal: 'Virtual Terminal'
    };
    return labels[source];
  };
  const getStatusBadge = (status: TransactionStatus) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800',
      partially_refunded: 'bg-orange-100 text-orange-800',
      cancelled: 'bg-gray-100 text-gray-600'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`} data-id="element-5340">
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>;
  };
  const filteredSales = sales.filter(sale => {
    const matchesSearch = sale.transactionId.toLowerCase().includes(searchTerm.toLowerCase()) || sale.customerName.toLowerCase().includes(searchTerm.toLowerCase()) || sale.customerEmail.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || sale.status === statusFilter;
    const matchesSource = sourceFilter === 'all' || sale.source === sourceFilter;
    return matchesSearch && matchesStatus && matchesSource;
  });
  const totalVolume = filteredSales.reduce((sum, sale) => sum + sale.amount, 0);
  const totalTransactions = filteredSales.length;
  const successfulTransactions = filteredSales.filter(s => s.status === 'completed').length;
  const successRate = totalTransactions > 0 ? Math.round(successfulTransactions / totalTransactions * 100) : 0;
  return <div className="space-y-6" data-id="element-5341">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-5342">
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-5343">
          <div className="text-sm font-medium text-gray-500" data-id="element-5344">Total Volume</div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-5345">
            Rs. {totalVolume.toLocaleString('en-LK')}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-5346">
          <div className="text-sm font-medium text-gray-500" data-id="element-5347">
            Total Transactions
          </div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-5348">
            {totalTransactions}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-5349">
          <div className="text-sm font-medium text-gray-500" data-id="element-5350">Successful</div>
          <div className="mt-2 text-3xl font-semibold text-green-600" data-id="element-5351">
            {successfulTransactions}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-5352">
          <div className="text-sm font-medium text-gray-500" data-id="element-5353">Success Rate</div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-5354">
            {successRate}%
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-5355">
        {/* Header */}
        <div className="p-6 border-b border-gray-200" data-id="element-5356">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-5357">
            <div className="flex-1 max-w-lg" data-id="element-5358">
              <div className="relative" data-id="element-5359">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} data-id="element-5360" />
                <input type="text" placeholder="Search transactions..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-5361" />
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-5362">
              <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-5363">
                <option value="all" data-id="element-5364">All Sources</option>
                <option value="payment_link" data-id="element-5365">Payment Link</option>
                <option value="qr_code" data-id="element-5366">QR Code</option>
                <option value="tap_to_phone" data-id="element-5367">Tap to Phone</option>
                <option value="invoice" data-id="element-5368">Invoice</option>
                <option value="pos" data-id="element-5369">POS Terminal</option>
                <option value="recurring" data-id="element-5370">Recurring</option>
                <option value="virtual_terminal" data-id="element-5371">Virtual Terminal</option>
              </select>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-5372">
                <option value="all" data-id="element-5373">All Status</option>
                <option value="completed" data-id="element-5374">Completed</option>
                <option value="pending" data-id="element-5375">Pending</option>
                <option value="processing" data-id="element-5376">Processing</option>
                <option value="failed" data-id="element-5377">Failed</option>
                <option value="refunded" data-id="element-5378">Refunded</option>
              </select>
              <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-5379">
                <DownloadIcon size={18} data-id="element-5380" />
                Export
              </button>
            </div>
          </div>
        </div>
        {/* Table */}
        <div className="overflow-x-auto" data-id="element-5381">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-5382">
            <thead className="bg-gray-50" data-id="element-5383">
              <tr data-id="element-5384">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5385">
                  Transaction ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5386">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5387">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5388">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5389">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5390">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5391">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-5392">
              {filteredSales.map(sale => <tr key={sale.id} className="hover:bg-gray-50" data-id="element-5393">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-5394">
                    <div className="text-sm font-medium text-gray-900" data-id="element-5395">
                      {sale.transactionId}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-5396">
                    <div className="text-sm text-gray-900" data-id="element-5397">
                      {new Date(sale.date).toLocaleDateString('en-GB')}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-5398">
                      {new Date(sale.date).toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-5399">
                    <div className="text-sm text-gray-900" data-id="element-5400">
                      {sale.customerName}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-5401">
                      {sale.customerEmail}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-5402">
                    <div className="flex items-center gap-2 text-sm text-gray-900" data-id="element-5403">
                      {getSourceIcon(sale.source)}
                      {getSourceLabel(sale.source)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-5404">
                    <div className="text-sm font-medium text-gray-900" data-id="element-5405">
                      Rs. {sale.amount.toLocaleString('en-LK')}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-5406">
                      Net: Rs. {sale.netAmount.toLocaleString('en-LK')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-5407">
                    {getStatusBadge(sale.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-5408">
                    <button onClick={() => navigate(`/sale-detail/${sale.id}`)} className="text-blue-600 hover:text-blue-900" title="View Details" data-id="element-5409">
                      <EyeIcon size={18} data-id="element-5410" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {/* Empty State */}
        {filteredSales.length === 0 && <div className="text-center py-12" data-id="element-5411">
            <p className="text-gray-500" data-id="element-5412">No transactions found</p>
          </div>}
      </div>
    </div>;
}